
<!--
  Attachment Viewer Modal Component Template
  =========================================
  
  This template provides a comprehensive attachment viewing interface that can be used
  in two contexts: within individual forms during submission and in form entry views
  when users click on existing attachments.
  
  Dual Usage Contexts:
  1. Form Submission: Renders within attachment upload section for previewing uploaded files
  2. Form Entry View: Renders as a modal when viewing existing form attachments
  
  Core Features:
  - Multi-format file support (images, videos, PDFs, documents, generic files)
  - Responsive design with desktop/mobile-specific controls
  - Image manipulation (zoom, pan, rotation) with touch gesture support
  - File navigation for multiple attachments
  - Download and external viewing options
  - Safe area handling for mobile devices
  
  Supported File Types:
  - Images: JPEG, PNG, GIF, BMP, TIFF, ICO, WebP with zoom/pan/rotation
  - Videos: MP4, MOV, WebM, OGG with native video controls
  - PDFs: Document preview with external viewing options
  - Documents: Word, TXT, RTF with file information display
  - Generic Files: Fallback display for unsupported types
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-card class="attachment-viewer-card ion-safe-area-bottom ion-safe-area-top ion-safe-area-left ion-safe-area-right" color="dark" >
    <ion-card-header
    class="attachment-header ion-no-padding-top">
      <ion-card-title class="attachment-title">
      
        {{ selectedAttachment?.name || 'Attachment' }}
      </ion-card-title>
      <ion-card-subtitle>
        <div class="attachment-controls" scrollX="true" scrollY="true" scrollEvent="true">
   
            <!-- Image Controls -->
            <div class="action-group" *ngIf="selectedAttachment && isImage(selectedAttachment)"
            scrollX="true" scrollY="true" scrollEvent="true">
          
              
              <!-- Desktop controls -->
              <ion-button-group *ngIf="!screenSizeService.isMobileDevice()">
                <ion-button fill="outline" color="primary" (click)="downloadFile()">
                  <ion-icon name="download-outline" slot="start"></ion-icon>
                  Download
                </ion-button>
                <ion-button fill="clear" color="light" (click)="resetZoom()">
                  <ion-icon name="contract-outline" slot="icon-only"></ion-icon>
                </ion-button>
                <ion-button fill="clear" color="light" (click)="zoomOut()" [disabled]="scale <= minScale">
                  <ion-icon name="remove-circle-outline" slot="icon-only"></ion-icon>
                </ion-button>
                <ion-button fill="clear" color="light" (click)="zoomIn()" [disabled]="scale >= maxScale">
                  <ion-icon name="add-circle-outline" slot="icon-only"></ion-icon>
                </ion-button>
                <ion-button fill="clear" color="light" (click)="rotateImage()">
                  <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-button-group>
              
              <!-- Mobile controls - simplified -->
              <ion-button-group *ngIf="screenSizeService.isMobileDevice()">
                <ion-button fill="outline" color="primary" (click)="downloadFile()">
                  <ion-icon name="download-outline" slot="start"></ion-icon>
                  Download
                </ion-button>
                <ion-button fill="clear" color="light" (click)="rotateImage()">
                  <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-button-group>
            </div>
    
             <!-- PDF/Document Controls -->
             <div class="action-group" *ngIf="selectedAttachment && (isPdf(selectedAttachment) || isDocument(selectedAttachment))">
              <ion-label color="medium">
                <h6>Document Options</h6>
              </ion-label>
              <div class="button-group">
                <ion-button fill="outline" color="primary" (click)="downloadFile()">
                  <ion-icon name="download-outline" slot="start"></ion-icon>
                  Download
                </ion-button>
                
                <ion-button fill="outline" color="primary" (click)="openInNewTab()">
                  <ion-icon name="open-outline" slot="start"></ion-icon>
                  Open
                </ion-button>
                
                
                <ion-button fill="clear" color="light" (click)="presentDocumentOptions()">
                  <ion-icon name="ellipsis-horizontal-outline" slot="icon-only"></ion-icon>
                </ion-button>
              </div>
            </div>
    
             <!-- Video Controls -->
             <div class="action-group" *ngIf="selectedAttachment && isVideo(selectedAttachment)">
              <ion-label color="medium">
                <h6>Video Options</h6>
              </ion-label>
              <div class="button-group">
                <ion-button fill="outline" color="primary" (click)="downloadFile()">
                  <ion-icon name="download-outline" slot="start"></ion-icon>
                  Download
                </ion-button>
              </div>
            </div>
            </div>
    
      
      </ion-card-subtitle>
    </ion-card-header>

  <!-- Main Content Card -->
  <ion-card-content class="card-content-container">

  
     <!-- Attachment Display -->
    
<div class="attachment-container">
      <div class="attachment-viewer"   #attachmentViewer *ngIf="selectedAttachment && selectedAttachment.url"
      [class.image-viewer]="isImage(selectedAttachment)"
      [class.video-viewer]="isVideo(selectedAttachment)"
      [class.document-viewer]="isPdf(selectedAttachment) || isDocument(selectedAttachment)"
      [class.file-viewer]="!isImage(selectedAttachment) && !isVideo(selectedAttachment) && !isPdf(selectedAttachment) && !isDocument(selectedAttachment)"
      [class.mobile]="screenSizeService.isMobileDevice()">
   
        
        <!-- Image Display with Zoom and Rotation -->
        <div class="image-wrapper" *ngIf="isImage(selectedAttachment)" (click)="toggleZoom($event)">
          <img 
            #attachmentImage
            [src]="selectedAttachment.url" 
            [alt]="selectedAttachment.name || 'Attachment'"
            class="attachment-image"
            [class.zoomed]="scale > 1"
            [style.transform]="getImageTransform()"
            (dblclick)="resetTransform()"
          >
        </div>

        <!-- Video Display -->
        <div class="video-wrapper" *ngIf="isVideo(selectedAttachment)">
          <ng-container *ngIf="!videoError">
            <video
              [src]="selectedAttachment.url"
              class="attachment-video"
              controls
              preload="metadata"
              (error)="onVideoError()"
            ></video>
          </ng-container>
          <ng-container *ngIf="videoError">
            <p class="unsupported-message">
              This video format cannot be previewed in your browser.
            </p>
          </ng-container>
        </div>
        

        <!-- PDF Display -->
        <div class="pdf-wrapper" *ngIf="isPdf(selectedAttachment)">
          <div class="pdf-fallback">
            <ion-card>
              <ion-card-header>
                <ion-card-title>
                  <ion-icon name="document-text-outline"></ion-icon>
                  PDF Document
                </ion-card-title>
                <ion-card-subtitle>{{ selectedAttachment.name }}</ion-card-subtitle>
              </ion-card-header>
              <ion-card-content>
                <p>{{ formatFileSize(selectedAttachment.size) }}</p>
                <div class="pdf-actions">
                  <ion-button 
                    expand="block" 
                    color="primary" 
                    (click)="openInNewTab()"
                    class="ion-margin-bottom"
                  >
                    <ion-icon name="open-outline" slot="start"></ion-icon>
                    Open PDF in New Tab
                  </ion-button>
                  <ion-button 
                    expand="block" 
                    fill="outline"
                    color="primary" 
                    (click)="downloadFile()"
                  >
                    <ion-icon name="download-outline" slot="start"></ion-icon>
                    Download PDF
                  </ion-button>
                </div>
              </ion-card-content>
            </ion-card>
          </div>
        </div>

        <!-- Document Display -->
        <div class="document-wrapper" *ngIf="isDocument(selectedAttachment)">
          <ion-card *ngIf="selectedAttachment.type !== 'text/plain'" class="document-card">
            <ion-card-header>
              <ion-card-title>
                <ion-icon [name]="getFileIcon(selectedAttachment)"></ion-icon>
                {{ getFileTypeDescription(selectedAttachment) }}
              </ion-card-title>
              <ion-card-subtitle>{{ selectedAttachment.name }}</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <p>{{ formatFileSize(selectedAttachment.size) }}</p>
              <p class="document-message">
                Document preview is not available for this file type.
              </p>
              <div class="document-actions">
                <ion-button 
                  expand="block" 
                  color="primary" 
                  (click)="presentDocumentOptions()"
                >
                  <ion-icon name="options-outline" slot="start"></ion-icon>
                  Document Options
                </ion-button>
              </div>
            </ion-card-content>
          </ion-card>
        </div>

        <!-- Generic File Display -->
        <div class="file-wrapper" 
             *ngIf="!isImage(selectedAttachment) && !isVideo(selectedAttachment) && !isPdf(selectedAttachment) && !isDocument(selectedAttachment)">
          <ion-card class="file-card">
            <ion-card-header>
              <ion-card-title>
                <ion-icon name="attach-outline"></ion-icon>
                File Attachment
              </ion-card-title>
              <ion-card-subtitle>{{ selectedAttachment.name }}</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <p>{{ formatFileSize(selectedAttachment.size) }}</p>
              <p class="file-message">Preview not available for this file type.</p>
              <ion-button 
                expand="block" 
                color="primary" 
                (click)="downloadFile()"
              >
                <ion-icon name="download-outline" slot="start"></ion-icon>
                Download File
              </ion-button>
            </ion-card-content>
          </ion-card>
        </div>

      </div>
    </div>
      
  


      <!-- Navigation (only show if multiple attachments) -->
      <ion-item class="attachment-navigation" *ngIf="attachmentList.length > 1" lines="none" color="dark">
        <ion-button 
          fill="clear" 
          color="light"
          [disabled]="currentAttachmentIndex === 0" 
          (click)="navigateAttachment(-1)"
          slot="start"
        >
          <ion-icon name="chevron-back-outline" slot="icon-only"></ion-icon>
        </ion-button>
        
        <ion-label class="ion-text-center">
          <ion-chip color="light" outline="true">
            <ion-label>{{ currentAttachmentIndex + 1 }} of {{ attachmentList.length }}</ion-label>
          </ion-chip>
        </ion-label>
        
        <ion-button 
          fill="clear" 
          color="light"
          [disabled]="currentAttachmentIndex === attachmentList.length - 1" 
          (click)="navigateAttachment(1)"
          slot="end"
        >
          <ion-icon name="chevron-forward-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </ion-item>


 
  </ion-card-content>
</ion-card>